<template>
  <div class="agenda" :style="'height:' + screenHeight + 'px'">
    <div class="container" :style="'width:'+ ((daysBefore + daysAfter) * 200) + 'px'">
      <div v-for="(day) in days" :key="day.start" :class="getDayStyle(day)" :id="new Date(day.start).toLocaleDateString()">
        <div class="day-display">
          <div class="head">
            <span>{{getDayName(day.start)}}</span> <span>{{new Date(day.start).toLocaleDateString()}}</span>
          </div>
        </div>
        <div class="hours">
          <div v-for="i in 24" :key="i" :style="getHourStyle(day, i)" class="hour" @click="createNewUsageClick">
            <span class="hour-text">{{pad(i - 1)}}:00 - {{pad(i)}}:00</span>
          </div>
        </div>
      </div>

      <div class="now-line" :style="nowLineStyle"></div>
      <div v-if="showMoveToTodayButton" class="move-to-today" @click="scrollToToday">Vandaag</div>
      <div v-if="showExampleUsage" :style="exampleUsageStyle" :class="exampleUsageClasses" ref="exampleUsage"></div>
      <div class="add" @click="createNewUsageFromNothingClick">+</div>

      <div v-for="usage in usages" :key="usage.id"
        :style="getUsageStyle(usage)"
        :class="getUsageClasses(usage)"
        @click="openDetails(usage, $event)"
        :id="'id-' + usage.id">
        <div class="from-to">
          {{new Date(usage.fromTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}} -
          {{new Date(usage.toTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}}
        </div>
        <div class="user">
          {{usage.userName}}
        </div>
      </div>

    </div>

    <usage-details v-if="detailsOpened" :usage="openedUsage" @close="closeDetails()" @update="onUpdated" @deleted="onDeleted" @deletedRecurring="onDeletedRecurring"></usage-details>
    <create-usage v-if="creatingUsage" :startTimestamp="startTimestamp" :endTimestamp="endTimestamp" :user="user" @created="onCreated" @close="closeCreateUsage()"></create-usage>
  </div>
</template>

<script>

import jwt_decode from 'jwt-decode'

import UsageDetails from './UsageDetails'
import CreateUsage from './CreateUsage'

const HOUR = 1000 * 3600;
const DAY = HOUR * 24;
const WEEK = DAY * 7;

export default {
	name: 'Agenda',
	components: {
		'usage-details': UsageDetails,
		'create-usage': CreateUsage,
	},
	data () {
		return {
      userId: parseInt(jwt_decode(localStorage.getItem('token')).id),
      user: {},
      ready: false,
      usages: [],
      now: Date.now(),
      daysBefore: 10,
      daysAfter: 20,
      today: new Date().setHours(0, 0, 0, 0),
      screenHeight: 0,
      showMoveToTodayButton: false,
      hourStyle: '',
      detailsOpened: false,
      openedUsage: {},
      drag: null,
      creatingUsage: false,
      showExampleUsage: false,
      exampleUsageHeight: 0,
      startTimestamp: 0,
      endTimestamp: 0
		}
	},
	methods: {
		onDeleted(id) {
        const deletedUsageIndex = this.usages.map(usage => usage.id).indexOf(id);
        this.usages.splice(deletedUsageIndex, 1);
		},
    onDeletedRecurring(id) {
      this.getUsage();
    },
		onCreated() {
      this.getUsage();
		},
    createNewUsageClick(e) {
      const position = cumulativeOffset(e.path[0]);
      const timestamp = this.getTimestampFromPosition(position.x, position.y)
      this.createNewUsage(timestamp, timestamp);
    },
		createNewUsage(startTimestamp = 0, endTimestamp = 0) {
      if (this.creatingUsage) return;

      const coefficient = 1000 * 60 * 30;

      this.creatingUsage = true;
      this.startTimestamp = Math.round(startTimestamp / coefficient) * coefficient;
      this.endTimestamp = Math.round(endTimestamp / coefficient) * coefficient;
		},
    createNewUsageFromNothingClick() {
      this.createNewUsageFromNothing();
    },
    createNewUsageFromNothing() {
      this.creatingUsage = true;
    },
		closeCreateUsage() {
      this.creatingUsage = false;
      this.startTimestamp = 0;
      this.endTimestamp = 0;
		},
		onUpdated(newUsage) {
      const updatedUsageIndex = this.usages.map(usage => usage.id).indexOf(newUsage.id);
      this.usages[updatedUsageIndex] = {...this.usages[updatedUsageIndex], ...newUsage};

      setTimeout(() => {
        document.getElementById('id-' + newUsage.id).scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'})
      }, 20)
		},
		closeDetails() {
      this.detailsOpened = false;
      this.openedUsage = {};
		},
		openDetails(usage, e) {
      this.detailsOpened = true;
      this.openedUsage = usage;
      e.path[0].scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'})
		},
		getDayStyle(day) {
      let style = `day`;

      if (day.isToday) style += ' today';

      return style;
		},
		getHourStyle(day, i) {
      let style = this.hourStyle;

      if (i === 1) style += ' opacity: 0';

      return style;
		},
		getUsageStyle(usage) {
      const usageDay = this.days
        .find(day => day.start <= usage.fromTimestamp && day.end >= usage.toTimestamp);

      if (!usageDay) {
        this.deleteUsage(usage)
        return `display: none`;
      }

      const usageDayIndex = this.days.map(day => day.start).indexOf(usageDay.start);

      const dayProgressRatio = (usage.fromTimestamp - usageDay.start) / (usageDay.end - usageDay.start)

      const x = (usageDayIndex * 200) + 50;
      const y = dayProgressRatio * this.screenHeight;
      const heightRatio = (usage.toTimestamp - usage.fromTimestamp) / (usageDay.end - usageDay.start)
      const height = (heightRatio * this.screenHeight) - 12;

      return `left: ${x}px; top: ${y}px; height: ${height}px; background-color: ${usage.userColour}`;
		},
		getUsageClasses(usage) {
      let classes = 'usage';

      if (usage.id === this.openedUsage.id) classes += ' opened';

      return classes;
		},
		getUsage() {
			const token = localStorage.getItem('token');
      this.global.GetBaseView().startLoad('Agenda ophalen...');

			this.axios.get(this.config.api + `/usages/${this.viewStart}/${this.viewEnd}`, {
        headers: {
        authorization: token
        }
      })
      .then(result => {
        this.usages = result.data;
        this.global.GetBaseView().endLoad();
      })
      .catch(() => {
        this.global.GetBaseView().endLoad();
        this.global.GetBaseView().logout();
      })
		},
		getUser() {
		const token = localStorage.getItem('token');

		this.axios.get(this.config.api + `/users/${this.userId}`, {
        headers: {
          authorization: token
        }
			})
			.then(result => {
			    this.user = result.data;
			})
			.catch(() => {

			})
		},
		deleteUsage(usage) {
			const token = localStorage.getItem('token');

			this.axios.delete(this.config.api + '/usages/' + usage.id, {
			headers: {
				authorization: token
			}
			})
			.then(() => {
				this.onDeleted(usage.id)
			})
			.catch((error) => {
        this.onDeleted(usage.id)
      })
		},
		scrollToToday() {
      this.showMoveToTodayButton = false;
      document.getElementById(new Date().toLocaleDateString()).scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'})
		},
		pad(d) {
			return (d < 10) ? '0' + d.toString() : d.toString();
		},
    getDayName(timestamp) {
      const dayMap = {
        0: 'Zo',
        1: 'Ma',
        2: 'Di',
        3: 'Wo',
        4: 'Do',
        5: 'Vr',
        6: 'Za'
      }
      const dayNumber = new Date(timestamp).getDay();

      return dayMap[dayNumber]
    },
    getTimestampFromPosition(x, y) {
      const totalDays = Math.round(x / 200);
      const totalDaysOffset = totalDays * DAY;
      const dayPercentage = y / this.screenHeight;
      const dayOffset = Math.round(DAY * dayPercentage);

      return this.viewStart + totalDaysOffset + dayOffset;
    }
	},
	computed: {
		days: function() {
			const days = [];
			const totalDays = this.daysBefore + this.daysAfter;
			const firstDayTimestamp = this.today - (DAY * this.daysBefore);

			for (let i = 0; i < totalDays; i++) {
				const start = firstDayTimestamp + (i * DAY);
				const end = start + DAY;
				const isToday = start < this.now && end > this.now;

				days.push({
					start,
					end,
					isToday
				})
			}

			return days;
		},
		nowLineStyle: function() {
			const today = this.days
				.find(day => day.start <= this.now && day.end > this.now);
			const todayIndex = this.days.map(day => day.start).indexOf(today.start);

			const dayProgressRatio = (this.now - today.start) / (today.end - today.start)

			const x = todayIndex * 200;
			const y = dayProgressRatio * this.screenHeight - 2;

			return `left: ${x}px; top: ${y}px`
		},
		viewStart: function() {
			return new Date().setHours(0, 0, 0, 0) - (this.daysBefore * DAY);
		},
		viewEnd: function() {
			return new Date().setHours(0, 0, 0, 0) + ((this.daysAfter * DAY) + 1) ;
		},
    exampleUsageStyle: function() {
      let x = this.drag.x;
      let y = this.drag.startY;
      let height = this.drag.y - this.drag.startY;

      if (this.drag.startY > this.drag.y) {
        y = this.drag.y;
        height = this.drag.startY - this.drag.y;
      }

      return `left: ${(x)}px; top: ${(y)}px; height: ${height}px; background-color: ${this.user.colour}`
    },
    exampleUsageClasses: function() {
      let classes = 'usage example-usage';

      if (this.creatingUsage) classes += ' opened'

      return classes;
    },
	},
	created() {
		this.getUsage();
		this.getUser();

		setInterval(() => {
			this.now = Date.now();
		}, 10000)
	},
	mounted() {
		this.screenHeight = window.innerHeight - (window.innerHeight / 50);
		this.hourStyle = 'height:' + Math.round((this.screenHeight / 24) - 1) + 'px;';

		document.addEventListener('mousedown', (e) => {
      this.showMoveToTodayButton = true;
		})

		document.addEventListener('touchstart', (e) => {
        this.showMoveToTodayButton = true;

        const start = cumulativeOffset(e.path[0]);

        this.drag = new Drag(start.x, e.targetTouches[0].pageY, (move) => {

          this.showExampleUsage = (move.y - move.startY) < -40;
          this.exampleUsageHeight = move.y - move.startY;
        }, (end) => {
          if (this.showExampleUsage) {
            const endTimestamp = this.getTimestampFromPosition(start.x, start.y)
            const startTimstamp = this.getTimestampFromPosition(end.x, end.y)

            this.createNewUsage(startTimstamp, endTimestamp);
            setTimeout(() => {
              this.showExampleUsage = false;
              this.exampleUsageHeight = 0;
            }, 1000)
          } else {
            this.showExampleUsage = false;
            this.exampleUsageHeight = 0;
          }
        })
		})

		document.addEventListener('touchmove', (e) => {
      this.drag.moveDrag(cumulativeOffset(e.path[0]).x, e.targetTouches[0].pageY);
		})

		document.addEventListener('touchend', (e) => {
      this.drag.endDrag();
		})

		setTimeout(() => {
		  this.scrollToToday();
		}, 100);
	},
}

class Drag {

  constructor(x, y, onMoveDrag, onEndDrag) {
    this.startX = x;
    this.startY = y;
    this.x = x;
    this.y = y;

    this.path = [{x,y}]

    this.onMoveDrag = onMoveDrag;
    this.onEndDrag = onEndDrag;
  }

  moveDrag(x, y) {
    this.x = x;
    this.y = y;

    this.path.push({x,y})

    this.onMoveDrag({
      x,
      y,
      startX: this.startX,
      startY: this.startY,
      path: this.path
    })
  }

  endDrag() {
    this.onEndDrag({
      x: this.x,
      y: this.y,
      startX: this.startX,
      startY: this.startY,
      path: this.path
    })
  }
}

const cumulativeOffset = (element) => {
    let top = 0, left = 0;
    do {
        top += element.offsetTop  || 0;
        left += element.offsetLeft || 0;
        element = element.offsetParent;
    } while(element);

    return {
        y: top,
        x: left
    };
};

</script>

<style scoped>

.close {

}

.agenda {
  overflow-y: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.container {
  overflow-y: hidden;
  display: flex;
  height: 100%;
  position: relative;
  user-select: none;
}

.day {
  width: 200px;
  position: relative;
}

.hours {
  top: 0;
  position: absolute;
}

.hour {
  width: 200px;
  border-top: 1px solid black;
}

.hour-text {
  font-size: 13px;
  margin-left: 10px;
}

.day-display {
  width: 190px;
  height: 100%;
  margin: 0 5px 0 5px;
  border-radius: 5px;
  background-color: rgba(255, 255, 255, 0.377);
}

.head {
  width: 100%;
  text-align: center;
}

.now-line {
  width: 200px;
  height: 4px;
  background-color: red;
  border-radius: 5px;
  position: absolute;
}

.move-to-today {
  position: fixed;
  top: 0;
  left: 5px;
  background-color: rgba(0, 0, 0, 0.452);
  padding: 2px 5px 3px 5px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  animation: popdown .5s 1;
  cursor: pointer;
}

@keyframes popdown {
  0% {
    top: -100%;
  }
  100% {
    top: 0;
  }
}

.usage {
  position: absolute;
  background-color: white;
  border-radius: 7px;
  width: 140px;
  color: black;
  padding: 6px;

  display: flex;
  flex-direction: column;

  transition: .5s;

  cursor: pointer;
}

.opened {
  animation: sizeup 1s 1;
}

@keyframes sizeup {
  0% {
    margin-left: 0;
    margin-top: 0;
  }
  100% {
    width: 2500px;
    height: 2500px;

    margin-left: -1000px;
    margin-top: -1000px;
  }
}

.from-to {
  font-size: 13px;
  width: 100%;
}

.user {
  font-size: 13px;
}

::-webkit-scrollbar {
  display: none;
}

.example-usage {
  transition: none;
}

.button {
  background-color: rgba(0, 0, 0, 0.514);
  color: white;

  border-radius: 50%;
  width: 50px;
  height: 50px;

  display: flex;
  justify-content: center;
  align-items: center;

  cursor: pointer;

  transition: .2s;
}

.fixed {
  position: fixed;
  top: 5px;
  left: 5px;
}

.add {
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.514);
  color: white;
  font-weight: 700;
  font-size: 20px;

  border-radius: 50%;
  width: 50px;
  height: 50px;

  display: flex;
  justify-content: center;
  align-items: center;

  cursor: pointer;

  transition: .2s;
}

</style>



// WEBPACK FOOTER //
// src/components/Agenda.vue